import { useState, useRef, useCallback, useEffect } from "react";

interface UseCameraReturn {
  stream: MediaStream | null;
  cameraActive: boolean;
  startCamera: () => Promise<void>;
  stopCamera: () => void;
  error: string | null;
  videoRef: React.RefObject<HTMLVideoElement>;
}

export function useCamera(): UseCameraReturn {
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const videoRef = useRef<HTMLVideoElement>(null!);
  const streamRef = useRef<MediaStream | null>(null);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setStream(null);
    setCameraActive(false);
  }, []);

  const startCamera = useCallback(async () => {
    setError(null);
    if (!navigator.mediaDevices?.getUserMedia) {
      setError("Camera is not available. Make sure you're using HTTPS and a supported browser.");
      return;
    }
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 1280 } },
        audio: false,
      });
      streamRef.current = mediaStream;
      setStream(mediaStream);
      setCameraActive(true);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (err: unknown) {
      const domErr = err as { name?: string; message?: string };
      if (domErr.name === "NotAllowedError" || domErr.name === "PermissionDeniedError") {
        setError("Camera access denied. Please allow camera access in your browser settings.");
      } else if (domErr.name === "NotFoundError") {
        setError("No camera found on this device.");
      } else {
        setError("Could not start camera: " + (domErr.message || domErr.name || "Unknown error"));
      }
    }
  }, []);

  // Attach stream to video element when it becomes available
  useEffect(() => {
    if (stream && videoRef.current && videoRef.current.srcObject !== stream) {
      videoRef.current.srcObject = stream;
    }
  }, [stream]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopCamera();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { stream, cameraActive, startCamera, stopCamera, error, videoRef };
}
